import React, { useMemo, useCallback } from "react";
import {
	Paper,
	Typography,
	Box,
	Button,
	Switch,
	FormControlLabel,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { RestoreSharp } from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { Alert } from "@mui/lab";
import { globalDefaultColDef, mesfTheme } from "@setup/ag-grid/mesf-theme";
import { useSearchUsageStatistics } from "./hooks/useSearchUsageStatistics";
import { StartFilter } from "./filters/StartFilter";
import { EndFilter } from "./filters/EndFilter";
import { SearchFilter } from "./filters/SearchFilter";
import { ComparisonStartFilter } from "./filters/ComparisonStartFilter";
import { ComparisonEndFilter } from "./filters/ComparisonEndFilter";

const useStyles = makeStyles(() => ({
	formControlLabel: {
		"& .MuiFormControlLabel-label": {
			fontSize: "0.875rem",
			fontWeight: 500,
			color: "#666",
		},
	},
}));

const cellStyles = {
	metric: {
		fontWeight: "500",
		color: "#333",
	},
	valueBase: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
	},
	valuePrimary: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
		color: "#1a73e8",
	},
	valueSecondary: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
		color: "#666",
	},
};

export const UsageStatistics: React.FC = () => {
	const classes = useStyles();
	const {
		statistics,
		comparisonStatistics,
		loading,
		comparisonLoading,
		error,
		comparisonError,
		filter,
		comparisonFilter,
		searchText,
		isComparisonMode,
		dateValidationErrors,
		refetch,
		refetchComparison,
		updateFilter,
		updateComparisonFilter,
		toggleComparisonMode,
		resetFilters,
		updateSearchText,
	} = useSearchUsageStatistics();

	const combinedStatistics = useMemo(() => {
		if (!isComparisonMode) return [];

		const firstPeriodMap = new Map(
			comparisonStatistics.map((stat) => [
				stat.Metric,
				parseFloat(stat.Value) || 0,
			]),
		);

		return statistics.map((secondStat) => {
			const firstValue = firstPeriodMap.get(secondStat.Metric) || 0;
			const secondValue = parseFloat(secondStat.Value) || 0;

			return {
				Metric: secondStat.Metric,
				FirstPeriodValue: firstValue,
				SecondPeriodValue: secondValue,
				AbsoluteChange: secondValue - firstValue,
			};
		});
	}, [statistics, comparisonStatistics, isComparisonMode]);

	const singlePeriodColumnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: "Metric",
				field: "Metric",
				flex: 3,
				sortable: true,
				filter: "agTextColumnFilter",
				wrapText: true,
				autoHeight: true,
				cellStyle: () => cellStyles.metric,
			},
			{
				headerName: "Value",
				field: "Value",
				flex: 1,
				sortable: true,
				filter: "agTextColumnFilter",
				cellStyle: () => cellStyles.valuePrimary,
			},
		],
		[],
	);

	const comparisonColumnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerName: "Metric",
				field: "Metric",
				flex: 3,
				sortable: true,
				filter: "agTextColumnFilter",
				wrapText: true,
				autoHeight: true,
				cellStyle: () => cellStyles.metric,
			},
			{
				headerName: "First Period",
				field: "FirstPeriodValue",
				flex: 1.5,
				sortable: true,
				filter: "agTextColumnFilter",
				cellStyle: () => cellStyles.valueSecondary,
				valueFormatter: (params) => {
					const value = params.value;
					if (typeof value === "number") {
						return value.toString();
					}
					return value || "0";
				},
			},
			{
				headerName: "Second Period",
				field: "SecondPeriodValue",
				flex: 1.5,
				sortable: true,
				filter: "agTextColumnFilter",
				cellStyle: () => cellStyles.valuePrimary,
				valueFormatter: (params) => {
					const value = params.value;
					if (typeof value === "number") {
						return value.toString();
					}
					return value || "0";
				},
			},
			{
				headerName: "Change (Second - First)",
				field: "AbsoluteChange",
				flex: 2,
				sortable: true,
				filter: "agTextColumnFilter",
				cellStyle: (params) => {
					const change = params.data?.AbsoluteChange || 0;
					const isIncrease = change > 0;
					const isDecrease = change < 0;

					return {
						...cellStyles.valueBase,
						color: isIncrease ? "#4caf50" : isDecrease ? "#f44336" : "#666",
					};
				},
				valueFormatter: (params) => {
					const change = params.value || 0;
					const sign = change > 0 ? "+" : "";

					return `${sign}${change}`;
				},
			},
		],
		[],
	);

	const handleReset = useCallback(() => {
		resetFilters();
	}, [resetFilters]);

	// No sizeColumnsToFit: it conflicts with `flex` (zeroes the column widths
	// when columns have flex but no explicit width). Flex alone fills the grid.

	if (error || comparisonError) {
		return (
			<Grid2 container spacing={3}>
				<Grid2 size={{ xs: 12 }}>
					<Paper style={{ padding: "24px", margin: "16px" }}>
						{error && (
							<Alert severity="error" style={{ marginBottom: "16px" }}>
								Failed to load usage statistics: {error}
							</Alert>
						)}
						{comparisonError && (
							<Alert severity="error" style={{ marginBottom: "16px" }}>
								Failed to load comparison statistics: {comparisonError}
							</Alert>
						)}
						<Box display="flex" style={{ gap: 16 }}>
							{error && (
								<Button
									variant="contained"
									color="primary"
									onClick={() => refetch()}
									startIcon={<RestoreSharp />}
								>
									Retry Current
								</Button>
							)}
							{comparisonError && (
								<Button
									variant="contained"
									color="primary"
									onClick={() => refetchComparison()}
									startIcon={<RestoreSharp />}
								>
									Retry Comparison
								</Button>
							)}
						</Box>
					</Paper>
				</Grid2>
			</Grid2>
		);
	}

	return (
		<Grid2 container spacing={3} style={{ padding: "16px" }}>
			<Grid2 size={{ xs: 12 }}>
				<Typography
					variant="h5"
					style={{ margin: "0 0 20px", fontWeight: 600 }}
				>
					System Usage Statistics
				</Typography>
				<Paper style={{ padding: "24px" }}>
					<Box mb={3}>
						<Paper style={{ padding: "16px", backgroundColor: "#f8f9fa" }}>
							<Grid2
								container
								spacing={2}
								alignItems="center"
								justifyContent="space-between"
							>
								{isComparisonMode ? (
									<>
										<Grid2 size={{ xs: 12, sm: 6, md: 5 }}>
											<Typography variant="subtitle2" color="primary">
												First Period
											</Typography>
											<Grid2 container spacing={1}>
												<Grid2 size={{ xs: 6 }}>
													<ComparisonStartFilter
														start={comparisonFilter.start}
														end={comparisonFilter.end}
														onStartChange={(start) =>
															updateComparisonFilter({ start })
														}
														error={
															dateValidationErrors.comparisonStartDateError
														}
													/>
												</Grid2>
												<Grid2 size={{ xs: 6 }}>
													<ComparisonEndFilter
														end={comparisonFilter.end}
														start={comparisonFilter.start}
														onEndChange={(end) =>
															updateComparisonFilter({ end })
														}
														error={dateValidationErrors.comparisonEndDateError}
													/>
												</Grid2>
											</Grid2>
										</Grid2>
										<Grid2 size={{ xs: 12, sm: 6, md: 5 }}>
											<Typography variant="subtitle2" color="primary">
												Second Period
											</Typography>
											<Grid2 container spacing={1}>
												<Grid2 size={{ xs: 6 }}>
													<StartFilter
														start={filter.start}
														end={filter.end}
														onStartChange={(start) => updateFilter({ start })}
														error={dateValidationErrors.startDateError}
													/>
												</Grid2>
												<Grid2 size={{ xs: 6 }}>
													<EndFilter
														end={filter.end}
														start={filter.start}
														onEndChange={(end) => updateFilter({ end })}
														error={dateValidationErrors.endDateError}
													/>
												</Grid2>
											</Grid2>
										</Grid2>
										<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
											<Button
												variant="contained"
												color="primary"
												startIcon={<RestoreSharp />}
												onClick={handleReset}
												disabled={loading}
												fullWidth
												style={{
													height: "38px",
													marginTop: "32px",
												}}
											>
												RESET
											</Button>
										</Grid2>
									</>
								) : (
									<>
										<Grid2 size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
											<Typography variant="subtitle2" color="primary">
												Period
											</Typography>
											<Grid2 container spacing={2}>
												<Grid2 size={{ xs: 12, sm: 6 }}>
													<StartFilter
														start={filter.start}
														end={filter.end}
														onStartChange={(start) => updateFilter({ start })}
														error={dateValidationErrors.startDateError}
													/>
												</Grid2>
												<Grid2 size={{ xs: 12, sm: 6 }}>
													<EndFilter
														end={filter.end}
														start={filter.start}
														onEndChange={(end) => updateFilter({ end })}
														error={dateValidationErrors.endDateError}
													/>
												</Grid2>
											</Grid2>
										</Grid2>
										<Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 2 }}>
											<Button
												variant="contained"
												color="primary"
												startIcon={<RestoreSharp />}
												onClick={handleReset}
												disabled={loading}
												fullWidth
												style={{
													height: "38px",
													marginTop: "32px",
												}}
											>
												RESET
											</Button>
										</Grid2>
									</>
								)}
							</Grid2>
						</Paper>
					</Box>

					<Box mb={0}>
						<Grid2 container spacing={1} alignItems="center">
							<Grid2 size={{ xs: 12, sm: 9, md: 10 }}>
								<SearchFilter
									searchText={searchText}
									onSearchChange={updateSearchText}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 3, md: 2 }}>
								<Box
									display="flex"
									alignItems="flex-end"
									justifyContent="center"
									height="45px"
									width="100%"
									paddingBottom="2px"
								>
									<Paper
										elevation={0}
										style={{
											padding: "6px 10px",
											backgroundColor: "#f8f9fa",
											borderRadius: "4px",
											border: "1px solid #e0e0e0",
											width: "100%",
											height: "38px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<FormControlLabel
											control={
												<Switch
													checked={isComparisonMode}
													onChange={toggleComparisonMode}
													color="primary"
													size="small"
												/>
											}
											label="Compare Periods"
											labelPlacement="start"
											style={{
												margin: 0,
												fontSize: "0.8rem",
												textAlign: "center",
											}}
											className={classes.formControlLabel}
										/>
									</Paper>
								</Box>
							</Grid2>
						</Grid2>
					</Box>

					{isComparisonMode ? (
						<div
							style={{
								height: "calc(100vh - 400px)",
								width: "100%",
								minHeight: "500px",
								border: "1px solid #e0e0e0",
								borderRadius: "8px",
							}}
						>
							<AgGridReact
								theme={mesfTheme}
								rowData={combinedStatistics}
								columnDefs={comparisonColumnDefs}
								defaultColDef={globalDefaultColDef}
								enableCharts={true}
								animateRows={true}
								getRowId={(params) => params.data.Metric}
								loading={loading || comparisonLoading}
								headerHeight={45}
								rowHeight={40}
								enableCellTextSelection={false}
								ensureDomOrder={true}
								suppressCellFocus={true}
								suppressRowHoverHighlight={false}
								suppressHorizontalScroll={false}
							/>
						</div>
					) : (
						<div
							style={{
								height: "calc(100vh - 400px)",
								width: "100%",
								minHeight: "500px",
								border: "1px solid #e0e0e0",
								borderRadius: "8px",
							}}
						>
							<AgGridReact
								theme={mesfTheme}
								rowData={statistics}
								columnDefs={singlePeriodColumnDefs}
								defaultColDef={globalDefaultColDef}
								enableCharts={true}
								animateRows={true}
								getRowId={(params) => params.data.Metric}
								loading={loading}
								headerHeight={45}
								rowHeight={40}
								enableCellTextSelection={false}
								ensureDomOrder={true}
								suppressCellFocus={true}
								suppressRowHoverHighlight={false}
								suppressHorizontalScroll={true}
							/>
						</div>
					)}
				</Paper>
			</Grid2>
		</Grid2>
	);
};
